<script lang="ts" setup>
import ChatBotBox from '@/components/navBar/aiassistant/ChatBotBox.vue'
import {nextTick, onMounted, ref} from 'vue'
import {copyToClipboard, useDialogPluginComponent} from "quasar";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import DislikeIcon from "@/assets/editViewIcons/DislikeIcon.vue";
import Translator from "@/utils/common/Translator.ts";
import DialogCloseIcon from "@/assets/editViewIcons/DialogCloseIcon.vue";
import ClientCache from "@/utils/login/ClientCache.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {QMarkdown} from '@quasar/quasar-ui-qmarkdown'
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {getAISelectedModel, setAISelectedModel} from "@/composables/UseChatModel.ts";
import AIChatMessageType from "@/utils/constants/AIChatMessageType.ts";
import UserFeedback from "@/utils/constants/UserFeedback.ts";
import ChatBotToolsSidebar from "@/components/navBar/aiassistant/ChatBotToolsSidebar.vue";
import ApprovalIcon from "@/components/icons/navBarIcons/ApprovalIcon.vue";
import DTOAIChatSession = namaTypes.DTOAIChatSession;
import DTOAIChatMessage = namaTypes.DTOAIChatMessage;
import {useLoginInfoStore} from "@/stores/loginInfo.ts";

const {dialogRef, onDialogCancel} = useDialogPluginComponent();
const modelDropdownOpen = ref(false);
const props = defineProps({
  header: {
    type: String,
    default: true
  },
  initialMessages: {
    type: Array as () => Array<DTOAIChatMessage>,
    default: () => []
  }
})
const aiTyping = ref(false)
const showHeader = ref<boolean>(!useNamaFlagsStore().aiChatMessages.length)
const chatId = ref<string>()
const showLoading = ref(false);
const isHistoryOpen = ref(false);
const isMiniState = ref(false);
const isRtl = ref(Translator.isLanguageRTL());
const chatHistoryList = ref<DTOAIChatSession[]>([]);
const selectedChat = ref<DTOAIChatSession | null>(null);
const chatModels = ref<string[]>([]);
const selectedModel = ref<string>("");
const chatContainer = ref<HTMLElement | null>(null);
const isExpertMode = ref(false);
const allMessages = ref<DTOAIChatMessage[]>([]);

async function fetchChatModels() {
  try {
    const resp = await NamaCommon.namaFetch(NamaURLS.FETCH_DEFINED_CHAT_MODELS, {
      noLoadingIndicator: true
    });
    chatModels.value = resp ?? [];

    const savedModel = getAISelectedModel();
    if (savedModel && chatModels.value.includes(savedModel)) {
      selectedModel.value = savedModel;
    } else if (chatModels.value.length > 0) {
      selectedModel.value = chatModels.value[0] ?? "";
    }
  } catch (error) {
    chatModels.value = [];
  }
}

async function fetchChatSessions() {
  const sessions = await NamaCommon.namaFetch(NamaURLS.FETCH_CHAT_SESSIONS, {
    noLoadingIndicator: true, data: {expertMode: isExpertMode.value}
  })
  chatHistoryList.value = sessions || []
}

async function fetchChatMessages(sessionId: string) {
  showLoading.value = true
  const messages: DTOAIChatMessage[] = await NamaCommon.namaFetch(NamaURLS.FETCH_CHAT_HISTORY, {
    data: {id: sessionId},
    noLoadingIndicator: true
  })
  if (messages && messages.length) {
    allMessages.value = messages;
    useNamaFlagsStore().aiChatMessages = messages
      .filter(msg => msg.content && msg.messageType !== AIChatMessageType.System && msg.messageType !== AIChatMessageType.ToolExecutionResult);
    chatId.value = sessionId
    useNamaFlagsStore().aiChatId = sessionId
    showHeader.value = useNamaFlagsStore().aiChatMessages.length === 0
  }
}

async function selectChatSession(session: DTOAIChatSession) {
  selectedChat.value = session
  await fetchChatMessages(session.id ?? "")
}

async function scrollToNewMessage() {
  await nextTick();
  await nextTick();

  setTimeout(() => {
    if (!chatContainer.value) return;
    const messages = useNamaFlagsStore().aiChatMessages;
    if (messages.length === 0) return;
    const lastMessageIndex = messages.length - 1;
    const messageElements = chatContainer.value.querySelectorAll('.tw-w-full, .q-chat-message');
    if (messageElements.length > lastMessageIndex) {
      const targetElement = messageElements[lastMessageIndex] as HTMLElement;
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }
  }, 150);
}

async function scrollToBottom() {
  await nextTick();
  await nextTick();

  setTimeout(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTo({
        top: chatContainer.value.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, 100);
}

function selectModel(model: string) {
  selectedModel.value = model || ""
  setAISelectedModel(model);
  modelDropdownOpen.value = false
}


async function createNewChatId() {
  chatId.value = await ClientCache.asyncNextGUIDId();
  useNamaFlagsStore().aiChatId = chatId.value || "";
}

onMounted(async () => {
  await fetchChatSessions();
  await fetchChatModels();
  if (!useNamaFlagsStore().aiChatId) {
    await createNewChatId();
  } else {
    chatId.value = useNamaFlagsStore().aiChatId;
    await fetchChatMessages(chatId.value);
  }
  if (props.initialMessages.length > 0) {
    showHeader.value = false;
    const filteredMessages = props.initialMessages.filter(msg => msg.messageType !== AIChatMessageType.System && msg.messageType !== AIChatMessageType.ToolExecutionResult);
    filteredMessages.forEach(msg => {
      addMessage(msg);
    });
    if (filteredMessages.length === 0) {
      showHeader.value = true;
    }
  }
})

const addMessage = async (message: DTOAIChatMessage) => {
  allMessages.value.push(message);
  useNamaFlagsStore().updateAIChatMessage(message);
  await nextTick();

  if (message.messageType == AIChatMessageType.User) {
    await scrollToBottom();
  } else {
    await scrollToNewMessage();
  }
};

const toggleDirection = () => {
  isRtl.value = !isRtl.value;
}

const toggleExpertMode = () => {
  isExpertMode.value = !isExpertMode.value;
}
interface ChatAttachment {
  fileName: string;
  mimeType: string;
  size: number;
  status: 'uploading' | 'ready' | 'error';
  fileId?: string;
  providerName?: string;
  errorMessage?: string;
}

async function handleSend(message: string, attachment: ChatAttachment | null) {
  showHeader.value = false;
  const readyAttachment = attachment && attachment.status === 'ready' ? attachment : null;
  const displayedContent = readyAttachment
    ? (message ? `📎 ${readyAttachment.fileName}\n${message}` : `📎 ${readyAttachment.fileName}`)
    : message;
  await addMessage({messageType: AIChatMessageType.User, content: displayedContent});
  aiTyping.value = true;
  showLoading.value = true;

  try {
    const req: namaTypes.AIPromptReq = {
      promptText: message,
      chatId: chatId.value,
      selectedChatModel: selectedModel.value,
      attachments: [],
      attachmentFileIds: readyAttachment && readyAttachment.fileId ? [readyAttachment.fileId] : [],
      attachmentsProviderName: readyAttachment?.providerName
    };

    let result: namaTypes.AIPromptResp;
    try {
      result = await NamaCommon.namaFetch(NamaURLS.SEND_AI_MSG, {
        data: NamaCommon.stringify(req),
        noLoadingIndicator: true,
      });
    } finally {
      await fetchChatSessions();
      showLoading.value = false;
    }
    await addMessage({id: result.messageId, content: result.response, messageType: AIChatMessageType.Assistant});
    if (isExpertMode.value)
      await fetchChatMessages(chatId.value ?? '');
  } catch (error) {
    console.error('API call failed:', error);
    await addMessage({messageType: AIChatMessageType.Assistant, content: "An error occurred, please review system logs"});
  } finally {
    aiTyping.value = false;
  }
}

async function sendFeedbackToBackend(messageId: string | undefined, isPositive: boolean) {
  if (!messageId) {
    console.warn('Missing messageId, cannot send feedback.');
    return;
  }

  if (!chatId.value) {
    console.warn('Missing chatId, cannot send feedback.');
    return;
  }

  try {
    const feedback = isPositive ? UserFeedback.Like : UserFeedback.Dislike;
    let request: DTOAIChatMessage = {id: messageId, userFeedback: feedback};
    await NamaCommon.namaFetch(NamaURLS.SEND_USER_FEEDBACK_FOR_AI_ANSWER, {
      data: request,
      noLoadingIndicator: true
    });

    console.log('Feedback sent successfully');
    const messages = useNamaFlagsStore().aiChatMessages;
    const messageToUpdate = messages.find(msg => msg.id === messageId);
    if (messageToUpdate) {
      messageToUpdate.userFeedback = feedback;
    }

  } catch (error) {
    console.error('Failed to send feedback:', error);
  }
}
async function handleCopyMessage(messageText?: string) {
  if (messageText)
    await copyToClipboard(messageText);
}

async function startNewChat() {
  useNamaFlagsStore().clearChatHistory();
  selectedChat.value = null;
  await createNewChatId();
  showHeader.value = true;
}

async function toggleChatHistory() {
  isHistoryOpen.value = !isHistoryOpen.value;
  if (isHistoryOpen.value) {
    isMiniState.value = false;
    await fetchChatSessions();
  }
}

async function toggleMiniState() {
  isMiniState.value = !isMiniState.value;
  if (isMiniState.value)
    isHistoryOpen.value = false;
  else
    await toggleChatHistory();
}

async function onCancelClick() {
  onDialogCancel();
}

</script>

<template>
  <q-dialog v-if="useLoginInfoStore().isLoggedIn" ref="dialogRef" @hide="onCancelClick" @keydown.esc="onCancelClick">
    <q-layout view="hHh Lpr lff" container style="min-height: 100%; max-height: 100%;"
              class="nama-chatAi-layout tw-border-rounded tw-shadow-none tw-overflow-hidden dark:tw-bg-namaBackground-deep-dark tw-bg-namaBackground-deep-light tw-h-full tw-w-full">
      <q-header class="tw-text-xs tw-text-namaFontColor-input-light tw-bg-[#CED4DA] dark:tw-bg-[#212529] dark:tw-text-white tw-h-fit">
        <q-toolbar>
          <NamaIcon default-icon="nm-menu" id="chatToolsSideBarBtn" class="tw-size-5 tw-cursor-pointer desktop:tw-hidden" @click.stop="isMiniState=!isMiniState"/>
          <q-toolbar-title v-text="Translator.translateId('Nama AI Assistant')" class="mobile:tw-hidden"/>
          <q-btn-dropdown :label="selectedModel || 'Choose a model'" no-caps>
            <q-list style="min-width: 200px">
              <q-item v-for="model in chatModels" :key="model" clickable v-close-popup @click="selectModel(model)">
                <q-item-section>
                  <q-item-label>{{ model }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
          <q-space/>
          <q-btn dense flat @click="onCancelClick" :title="Translator.translateId('close')"
                 class="nama-btn-hover nama-custom-btn focus:tw-bg-transparent">
            <q-avatar square class="tw-w-5">
              <DialogCloseIcon/>
            </q-avatar>
          </q-btn>
        </q-toolbar>
      </q-header>
      <ChatBotToolsSidebar
        :is-history-open="isHistoryOpen"
        :is-expert-mode="isExpertMode"
        :is-rtl="isRtl"
        :chat-history-list="chatHistoryList"
        :selected-chat="selectedChat"
        :isMiniState="isMiniState"
        @start-new-chat="startNewChat"
        @toggle-history="toggleChatHistory"
        @toggle-direction="toggleDirection"
        @toggle-expert-mode="toggleExpertMode"
        @select-chat-session="selectChatSession"
        @toggle-mini-state="toggleMiniState"
      />
      <q-page-container class="tw-h-full tw-w-full !tw-pt-0 tw-overflow-hidden">
        <q-page class="tw-flex tw-flex-col tw-gap-2 tw-overflow-hidden tw-h-full tw-w-full tw-pt-12">
          <q-card-section class="tw-w-full tw-flex-1 tw-min-h-0">
            <div class="tw-flex tw-w-full tw-h-full tw-mx-auto" :dir="isRtl ? 'rtl' : 'ltr'">
              <div class="tw-flex-1 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
                <div class="tw-flex-1 tw-h-full tw-w-full tw-overflow-y-auto tw-overflow-x-hidden" ref="chatContainer">
                  <div class="tw-flex-1 tw-h-full tw-w-full tw-px-6 tw-pt-6">
                    <div v-if="showHeader"
                         class="tw-content-center tw-text-center tw-text-xl tw-font-semibold tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark tw-h-full tw-py-4"
                  >
                      <span v-text="header"/>
                    </div>
                    <template v-else>
                      <div v-for="(msg, index) in (isExpertMode ? allMessages : useNamaFlagsStore().aiChatMessages)" :key="index" class="tw-w-full"
                           :class="msg.messageType === AIChatMessageType.User ? 'tw-flex tw-justify-end' : 'tw-flex tw-justify-start'">
                        <div v-if="isExpertMode"
                             class="tw-w-full tw-mb-3 tw-p-3 tw-rounded-lg tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"
                             :class="{
                                       'tw-bg-blue-100 dark:tw-bg-blue-900': msg.messageType === AIChatMessageType.User,
                                       'tw-bg-green-100 dark:tw-bg-green-900': msg.messageType === AIChatMessageType.Assistant,
                                       'tw-bg-yellow-100 dark:tw-bg-yellow-900': msg.messageType === AIChatMessageType.System,
                                       'tw-bg-purple-100 dark:tw-bg-purple-900': msg.messageType === AIChatMessageType.ToolExecutionResult
                                     }">
                          <div class="tw-font-bold tw-text-xs tw-mb-2 tw-uppercase">{{ msg.messageType }}</div>
                          <pre class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono">{{ msg.content }}</pre>
                          <pre v-if="msg.metadata" class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono" style="direction: ltr">{{
                              msg.metadata
                            }}</pre>
                        </div>
                        <template v-else>
                          <q-chat-message v-if="msg.messageType === AIChatMessageType.User" :text="[msg.content]" :dir="isRtl ? 'rtl' : 'ltr'"
                                          :sent="true" class="nama-chatBot-message tw-max-w-[75%] tw-py-1.5 tw-text-base" bg-color="primary"
                                          text-color="white"/>
                          <div v-else
                               class="tw-pb-2 tw-pt-0.5 tw-bg-grey-3 tw-rounded-lg tw-max-w-[75%] tw-text-sm tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark">
                            <div class="nama-chat-markdown tw-px-3" dir="auto">
                              <q-markdown :src="msg.content" no-html/>
                            </div>
                            <div class="tw-flex tw-justify-end tw-pt-2 tw-gap-1">
                              <q-btn
                                v-if="msg.id"
                                dense
                                flat
                                round
                                size="sm"
                                :class="{
                                          'tw-opacity-70 hover:tw-opacity-100 hover:tw-bg-green-100 dark:hover:tw-bg-green-900': msg.userFeedback !== UserFeedback.Like,
                                          'tw-opacity-100 tw-bg-green-100 dark:tw-bg-green-900 tw-text-green-700 dark:tw-text-green-300': msg.userFeedback ===  UserFeedback.Like
                                        }"
                                @click="sendFeedbackToBackend(msg.id, true)"
                                :title="Translator.translateId( UserFeedback.Like)">
                                <ApprovalIcon :has-fill="false" class="dark:tw-fill-white tw-w-4 tw-h-4"
                                              :class="{'tw-fill-green-600': msg.userFeedback ===  UserFeedback.Like}"/>
                              </q-btn>
                              <q-btn
                                v-if="msg.id"
                                dense
                                flat
                                round
                                size="sm"
                                :class="{
                                          'tw-opacity-70 hover:tw-opacity-100 hover:tw-bg-red-100 dark:hover:tw-bg-red-900': msg.userFeedback !==  UserFeedback.Dislike,
                                          'tw-opacity-100 tw-bg-red-100 dark:tw-bg-red-900 tw-text-red-700 dark:tw-text-red-300': msg.userFeedback ===  UserFeedback.Dislike
                                        }"
                                @click="sendFeedbackToBackend(msg.id, false)"
                                :title="Translator.translateId( UserFeedback.Dislike)"
                              >
                                <DislikeIcon class="tw-w-4 tw-h-4 dark:tw-fill-white"
                                             :class="{'tw-fill-red-600': msg.userFeedback ===  UserFeedback.Dislike}"/>
                              </q-btn>
                              <q-btn
                                dense
                                flat
                                round
                                size="sm"
                                class="tw-opacity-70 hover:tw-opacity-100 hover:tw-bg-blue-100 dark:hover:tw-bg-blue-900"
                                @click="handleCopyMessage(msg.content)"
                                :title="Translator.translateId('copy')"
                              >
                                <NamaIcon default-icon="nm-copy-to-clipboard" class="tw-w-4 tw-h-4"/>
                              </q-btn>
                            </div>
                          </div>
                        </template>
                      </div>
                    </template>
                    <!-- Typing Indicator -->
                    <div v-if="aiTyping" class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mt-2">
                      <q-spinner-dots size="24px" color="primary"/>
                      <span class="tw-text-sm tw-text-gray-500 dark:tw-text-gray-300">AI is typing...</span>
                    </div>
                    <div id="chat-element-to-scroll"/>
                  </div>
                </div>
              </div>
            </div>
          </q-card-section>
          <q-card-section class="tw-w-full tw-flex-shrink-0 tw-self-center tw-items-stretch tw-flex tw-flex-row">
            <ChatBotBox :chat-id="chatId" :selected-chat-model="selectedModel" @send="handleSend"/>
          </q-card-section>
        </q-page>
      </q-page-container>
    </q-layout>
  </q-dialog>
</template>
<style>
.nama-chatAi-layout.q-layout-container .absolute-full {
  @apply tw-h-full
}

.nama-chatAi-layout.q-layout-container .absolute-full .scroll {
  @apply tw-h-full tw-overflow-hidden
}

.nama-chatAi-layout.q-layout-container .absolute-full .scroll .q-layout {
  @apply tw-h-full
}

.nama-chatAi-layout.q-layout-container .absolute-full .scroll .q-layout .q-layout--containerized {
  @apply tw-h-full
}

.nama-chat-markdown { line-height: 1.55; word-wrap: break-word; }

.nama-chat-markdown h1,
.nama-chat-markdown h2,
.nama-chat-markdown h3,
.nama-chat-markdown h4,
.nama-chat-markdown h5,
.nama-chat-markdown h6 {
  font-weight: 600;
  margin: 0.9em 0 0.4em;
  line-height: 1.3;
}
.nama-chat-markdown h1 { font-size: 1.5em; }
.nama-chat-markdown h2 { font-size: 1.3em; }
.nama-chat-markdown h3 { font-size: 1.15em; }
.nama-chat-markdown h4 { font-size: 1em; }
.nama-chat-markdown h5 { font-size: 0.95em; }
.nama-chat-markdown h6 { font-size: 0.9em; color: #6b7280; }

.nama-chat-markdown p { margin: 0.55em 0; }

.nama-chat-markdown ul,
.nama-chat-markdown ol {
  margin: 0.4em 0;
  padding-inline-start: 1.6em;
}
.nama-chat-markdown ul { list-style: disc; }
.nama-chat-markdown ol { list-style: decimal; }
.nama-chat-markdown li { margin: 0.2em 0; }
.nama-chat-markdown li > ul,
.nama-chat-markdown li > ol { margin: 0.2em 0; }
.nama-chat-markdown li > p { margin: 0.2em 0; }

.nama-chat-markdown blockquote {
  margin: 0.6em 0;
  padding: 0.2em 0.9em;
  border-inline-start: 3px solid #9ca3af;
  color: #4b5563;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}
.tw-dark .nama-chat-markdown blockquote {
  color: #d1d5db;
  background: rgba(255, 255, 255, 0.05);
  border-inline-start-color: #6b7280;
}

.nama-chat-markdown code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92em;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.07);
  direction: ltr !important;
  unicode-bidi: isolate !important;
}
.tw-dark .nama-chat-markdown code { background: rgba(255, 255, 255, 0.12); }

.nama-chat-markdown pre,
.nama-chat-markdown pre *,
.nama-chat-markdown .q-markdown--code-wrapper,
.nama-chat-markdown .q-markdown--code-wrapper *,
.nama-chat-markdown .q-markdown--code,
.nama-chat-markdown .q-markdown--code *,
.nama-chat-markdown .q-markdown--code__inner,
.nama-chat-markdown .q-markdown--code__inner *,
.nama-chat-markdown .q-markdown--line-numbers-wrapper,
.nama-chat-markdown .q-markdown--line-numbers-wrapper * {
  direction: ltr !important;
  unicode-bidi: isolate !important;
  text-align: left !important;
}
.nama-chat-markdown pre {
  margin: 0.7em 0;
  padding: 0.8em 1em;
  border-radius: 8px;
  background: #1f2937;
  color: #f3f4f6;
  overflow-x: auto;
}
.nama-chat-markdown pre code {
  background: transparent;
  padding: 0;
  color: inherit;
  font-size: 0.9em;
  white-space: pre;
}

.nama-chat-markdown table {
  border-collapse: collapse;
  margin: 0.7em 0;
  width: 100%;
  font-size: 0.95em;
  display: block;
  overflow-x: auto;
}
.nama-chat-markdown th,
.nama-chat-markdown td {
  border: 1px solid #d1d5db;
  padding: 6px 10px;
  text-align: start;
}
.nama-chat-markdown th { background: rgba(0, 0, 0, 0.04); font-weight: 600; }
.tw-dark .nama-chat-markdown th,
.tw-dark .nama-chat-markdown td { border-color: #4b5563; }
.tw-dark .nama-chat-markdown th { background: rgba(255, 255, 255, 0.06); }

.nama-chat-markdown a {
  color: var(--nama-primary-light);
  text-decoration: underline;
  word-break: break-word;
}
.tw-dark .nama-chat-markdown a { color: var(--nama-primary-dark); }

.nama-chat-markdown hr {
  border: 0;
  border-top: 1px solid #d1d5db;
  margin: 0.9em 0;
}
.tw-dark .nama-chat-markdown hr { border-top-color: #4b5563; }

.nama-chat-markdown img { max-width: 100%; border-radius: 6px; }

.nama-chat-markdown strong { font-weight: 600; }
.nama-chat-markdown em { font-style: italic; }

.nama-chat-markdown > *:first-child { margin-top: 0; }
.nama-chat-markdown > *:last-child { margin-bottom: 0; }
</style>


